import { supabase } from '@/integrations/supabase/client';
import { getCourseQuizScores, calculateCourseQuizAverage } from '@/hooks/auth/progressService';
import CertificateService, { CertificateData, MarkSheetData } from '@/components/course/certificate/CertificateService';
import { generateMarksheetPDF } from '@/utils/marksheetPdfGenerator';
import JSZip from 'jszip';

export interface StaffCertificateData extends CertificateData {
  studentEmail?: string;
  issueDate: string;
}

export interface StaffMarksheetData extends MarkSheetData {
  studentId: string;
  studentEmail?: string;
  courseId: string;
}

class StaffDownloadService {
  /**
   * Fetch certificate data for any student (staff only)
   */
  static async getCertificateForStudent(
    studentId: string,
    courseId: string
  ): Promise<StaffCertificateData | null> {
    try {
      // FIX: Remove invalid FK JOIN syntax - certificates table has no FK to profiles
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', studentId)
        .eq('course_id', courseId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching certificate:', error);
        return null;
      }

      if (!data) return null;

      // Fetch student email separately since there's no FK relationship
      let studentEmail: string | undefined;
      const { data: profileData } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', studentId)
        .maybeSingle();
      
      if (profileData) {
        studentEmail = profileData.email;
      }

      const metadata = typeof data.metadata === 'object' && data.metadata !== null
        ? data.metadata as Record<string, any>
        : {};

      return {
        id: data.id,
        userId: data.user_id,
        courseId: data.course_id,
        courseTitle: data.course_title,
        certificateNumber: data.certificate_number,
        userName: data.user_name,
        quizAverage: metadata.quiz_average || 0,
        completionDate: new Date(data.issue_date).toLocaleDateString(),
        verificationHash: data.verification_hash || undefined,
        studentEmail,
        issueDate: data.issue_date,
      };
    } catch (error) {
      console.error('Error in getCertificateForStudent:', error);
      return null;
    }
  }

  /**
   * Fetch marksheet data for any student (staff only)
   */
  static async getMarksheetForStudent(
    studentId: string,
    courseId: string,
    courseTitle: string,
    studentName: string
  ): Promise<StaffMarksheetData | null> {
    try {
      // Get detailed quiz scores using existing service
      const quizScores = await getCourseQuizScores(studentId, courseId);
      const quizData = await calculateCourseQuizAverage(studentId, courseId);

      // Get certificate info if available
      const certificate = await this.getCertificateForStudent(studentId, courseId);

      // Get student email from profiles
      const { data: profileData } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', studentId)
        .maybeSingle();

      return {
        studentId,
        studentEmail: profileData?.email || undefined,
        courseId,
        userName: studentName,
        courseTitle,
        quizScores: quizScores.map(score => ({
          moduleName: score.moduleName,
          percentage: score.percentage,
          passed: score.passed,
          attemptDate: score.attemptDate,
          score: score.score,
          maxScore: score.maxScore,
        })),
        overallAverage: quizData.overallAverage,
        certificateNumber: certificate?.certificateNumber || 'N/A',
        completionDate: certificate?.completionDate || new Date().toLocaleDateString(),
      };
    } catch (error) {
      console.error('Error in getMarksheetForStudent:', error);
      return null;
    }
  }

  /**
   * Generate and download certificate PDF for a student
   */
  static async downloadCertificatePDF(
    certificateId: string
  ): Promise<boolean> {
    try {
      // Get certificate data
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('id', certificateId)
        .single();

      if (error || !data) {
        console.error('Certificate not found:', error);
        return false;
      }

      // Try to use existing certificate element if available
      const elementId = `certificate-${certificateId}`;
      const element = document.getElementById(elementId);
      
      if (element) {
        await CertificateService.generatePDF(elementId, `Certificate_${data.certificate_number}`);
        return true;
      }

      // If no element, generate a simple text-based PDF
      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const metadata = typeof data.metadata === 'object' && data.metadata !== null
        ? data.metadata as Record<string, any>
        : {};

      // Draw certificate border
      pdf.setDrawColor(0, 100, 180);
      pdf.setLineWidth(3);
      pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);

      // Inner border
      pdf.setLineWidth(1);
      pdf.rect(15, 15, pageWidth - 30, pageHeight - 30);

      // Title
      pdf.setFontSize(36);
      pdf.setTextColor(0, 100, 180);
      pdf.text('CERTIFICATE OF COMPLETION', pageWidth / 2, 45, { align: 'center' });

      // Subtitle
      pdf.setFontSize(16);
      pdf.setTextColor(100, 100, 100);
      pdf.text('NEO PRISM ACADEMY', pageWidth / 2, 58, { align: 'center' });

      // This certifies that
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('This certifies that', pageWidth / 2, 80, { align: 'center' });

      // Student Name
      pdf.setFontSize(28);
      pdf.setTextColor(0, 0, 0);
      pdf.text(data.user_name, pageWidth / 2, 95, { align: 'center' });

      // Has completed
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('has successfully completed', pageWidth / 2, 110, { align: 'center' });

      // Course Title
      pdf.setFontSize(22);
      pdf.setTextColor(0, 100, 180);
      pdf.text(data.course_title, pageWidth / 2, 125, { align: 'center' });

      // Grade
      const grade = metadata.quiz_average || 0;
      pdf.setFontSize(16);
      pdf.setTextColor(0, 150, 0);
      pdf.text(`with a grade of ${grade.toFixed(1)}%`, pageWidth / 2, 140, { align: 'center' });

      // Certificate Details
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Certificate Number: ${data.certificate_number}`, pageWidth / 2, 165, { align: 'center' });
      pdf.text(`Issue Date: ${new Date(data.issue_date).toLocaleDateString()}`, pageWidth / 2, 173, { align: 'center' });

      // Save PDF
      pdf.save(`Certificate_${data.certificate_number}.pdf`);
      return true;
    } catch (error) {
      console.error('Error downloading certificate PDF:', error);
      return false;
    }
  }

  /**
   * Generate and download marksheet PDF for a student
   * Includes validation and meaningful error messages
   */
  static async downloadMarksheetPDF(
    studentId: string,
    courseId: string,
    courseTitle: string,
    studentName: string
  ): Promise<boolean> {
    try {
      const marksheetData = await this.getMarksheetForStudent(
        studentId,
        courseId,
        courseTitle,
        studentName
      );

      if (!marksheetData) {
        console.error('[Staff Download] Unable to fetch marksheet data for student:', studentId);
        throw new Error('Unable to fetch marksheet data. Please try again.');
      }

      // Validate quiz scores exist before generating PDF
      if (!marksheetData.quizScores || marksheetData.quizScores.length === 0) {
        console.warn('[Staff Download] No quiz scores found for marksheet:', { studentId, courseId });
        throw new Error(`No quiz data available for ${courseTitle}. The student may not have completed any quizzes.`);
      }

      // Log data quality check
      const hasProperModuleNames = marksheetData.quizScores.every(
        score => score.moduleName && !score.moduleName.includes('undefined')
      );
      if (!hasProperModuleNames) {
        console.warn('[Staff Download] Some module names may be missing or malformed');
      }

      await generateMarksheetPDF(marksheetData);
      return true;
    } catch (error) {
      console.error('[Staff Download] Error downloading marksheet PDF:', error);
      // Re-throw with user-friendly message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to generate marksheet. Please try again later.');
    }
  }

  /**
   * Download all certificates for a student as ZIP
   */
  static async downloadAllCertificatesAsZip(
    studentId: string,
    studentName: string
  ): Promise<boolean> {
    try {
      const { data: certificates, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', studentId);

      if (error || !certificates || certificates.length === 0) {
        console.error('No certificates found for student');
        return false;
      }

      const zip = new JSZip();
      const jsPDF = (await import('jspdf')).default;

      for (const cert of certificates) {
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4',
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const metadata = typeof cert.metadata === 'object' && cert.metadata !== null
          ? cert.metadata as Record<string, any>
          : {};

        // Draw certificate border
        pdf.setDrawColor(0, 100, 180);
        pdf.setLineWidth(3);
        pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);
        pdf.setLineWidth(1);
        pdf.rect(15, 15, pageWidth - 30, pageHeight - 30);

        // Title
        pdf.setFontSize(36);
        pdf.setTextColor(0, 100, 180);
        pdf.text('CERTIFICATE OF COMPLETION', pageWidth / 2, 45, { align: 'center' });

        pdf.setFontSize(16);
        pdf.setTextColor(100, 100, 100);
        pdf.text('NEO PRISM ACADEMY', pageWidth / 2, 58, { align: 'center' });

        pdf.setFontSize(14);
        pdf.setTextColor(60, 60, 60);
        pdf.text('This certifies that', pageWidth / 2, 80, { align: 'center' });

        pdf.setFontSize(28);
        pdf.setTextColor(0, 0, 0);
        pdf.text(cert.user_name, pageWidth / 2, 95, { align: 'center' });

        pdf.setFontSize(14);
        pdf.setTextColor(60, 60, 60);
        pdf.text('has successfully completed', pageWidth / 2, 110, { align: 'center' });

        pdf.setFontSize(22);
        pdf.setTextColor(0, 100, 180);
        pdf.text(cert.course_title, pageWidth / 2, 125, { align: 'center' });

        const grade = metadata.quiz_average || 0;
        pdf.setFontSize(16);
        pdf.setTextColor(0, 150, 0);
        pdf.text(`with a grade of ${grade.toFixed(1)}%`, pageWidth / 2, 140, { align: 'center' });

        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Certificate Number: ${cert.certificate_number}`, pageWidth / 2, 165, { align: 'center' });
        pdf.text(`Issue Date: ${new Date(cert.issue_date).toLocaleDateString()}`, pageWidth / 2, 173, { align: 'center' });

        const pdfBlob = pdf.output('blob');
        zip.file(`Certificate_${cert.course_id}_${cert.certificate_number}.pdf`, pdfBlob);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificates_${studentName.replace(/\s+/g, '_')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error creating certificates ZIP:', error);
      return false;
    }
  }

  /**
   * Download all marksheets for a student as ZIP
   */
  static async downloadAllMarksheetsAsZip(
    studentId: string,
    studentName: string,
    courses: Array<{ courseId: string; courseTitle: string }>
  ): Promise<boolean> {
    try {
      const zip = new JSZip();
      const jsPDF = (await import('jspdf')).default;

      for (const course of courses) {
        const marksheetData = await this.getMarksheetForStudent(
          studentId,
          course.courseId,
          course.courseTitle,
          studentName
        );

        if (!marksheetData || marksheetData.quizScores.length === 0) continue;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        let yPos = 20;

        // Header
        pdf.setFontSize(24);
        pdf.setTextColor(0, 100, 180);
        pdf.text('NEO PRISM ACADEMY', pageWidth / 2, yPos, { align: 'center' });

        yPos += 10;
        pdf.setFontSize(18);
        pdf.setTextColor(60, 60, 60);
        pdf.text('ACADEMIC MARKSHEET', pageWidth / 2, yPos, { align: 'center' });

        yPos += 15;
        pdf.setDrawColor(0, 100, 180);
        pdf.setLineWidth(0.5);
        pdf.line(20, yPos, pageWidth - 20, yPos);

        // Student Info
        yPos += 10;
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Student Name: ${marksheetData.userName}`, 20, yPos);
        yPos += 7;
        pdf.text(`Course: ${marksheetData.courseTitle}`, 20, yPos);
        yPos += 7;
        pdf.text(`Certificate #: ${marksheetData.certificateNumber}`, 20, yPos);
        yPos += 7;
        pdf.text(`Completion Date: ${marksheetData.completionDate}`, 20, yPos);

        yPos += 15;
        pdf.setFontSize(14);
        pdf.setTextColor(0, 100, 180);
        pdf.text('MODULE BREAKDOWN', 20, yPos);

        // Table header
        yPos += 10;
        pdf.setFillColor(0, 100, 180);
        pdf.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.text('Module', 25, yPos);
        pdf.text('Score', 110, yPos);
        pdf.text('%', 140, yPos);
        pdf.text('Status', 165, yPos);

        // Table rows
        yPos += 8;
        pdf.setTextColor(0, 0, 0);
        marksheetData.quizScores.forEach((score, index) => {
          const bgColor = index % 2 === 0 ? 245 : 255;
          pdf.setFillColor(bgColor, bgColor, bgColor);
          pdf.rect(20, yPos - 4, pageWidth - 40, 7, 'F');

          const moduleName = score.moduleName.length > 40
            ? score.moduleName.substring(0, 37) + '...'
            : score.moduleName;
          pdf.text(moduleName, 25, yPos);
          pdf.text(`${score.score}/${score.maxScore}`, 110, yPos);
          pdf.text(`${score.percentage.toFixed(1)}%`, 140, yPos);

          if (score.passed) {
            pdf.setTextColor(0, 150, 0);
            pdf.text('PASSED', 165, yPos);
          } else {
            pdf.setTextColor(200, 0, 0);
            pdf.text('FAILED', 165, yPos);
          }
          pdf.setTextColor(0, 0, 0);
          yPos += 7;
        });

        // Overall average
        yPos += 10;
        pdf.setDrawColor(0, 100, 180);
        pdf.line(20, yPos, pageWidth - 20, yPos);
        yPos += 10;
        pdf.setFontSize(14);
        pdf.text(`OVERALL AVERAGE: ${marksheetData.overallAverage.toFixed(2)}%`, 20, yPos);
        yPos += 8;
        const passed = marksheetData.overallAverage >= 80;
        pdf.setTextColor(passed ? 0 : 200, passed ? 150 : 0, 0);
        pdf.text(`RESULT: ${passed ? 'PASSED' : 'FAILED'}`, 20, yPos);

        const pdfBlob = pdf.output('blob');
        zip.file(`Marksheet_${course.courseId}.pdf`, pdfBlob);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Marksheets_${studentName.replace(/\s+/g, '_')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error creating marksheets ZIP:', error);
      return false;
    }
  }
}

export default StaffDownloadService;
